import { z } from 'zod'
import assert from 'assert'
import {
  ResultOrError,
  ValueObject,
} from '@lukso/controllerapps-sharedlogic-ddd-core'
import { Address } from '@lukso/controllerapps-sharedlogic-primitives'

import { Account } from './Account'
import { TransactionData } from '../transaction/TransactionData'
import { EIP191Signature } from './EIP191Signature'
import { TransactionRequest } from '../transaction/TransactionRequest'
import { SignedTransaction } from '../transaction/SignedTransaction'
import { MessageSigningRequest } from '../message-signing/MessageSigningRequest'
import { SignedMessage } from '../message-signing/SignedMessage'
import { AccountNotFoundError } from './errors/AccountNotFoundError'
import { KeyringSubdomainError } from '../errors/KeyringSubdomainError'

const WalletPropsSchema = z.object({
  accounts: z.array(z.instanceof(Account)),
})

type WalletProps = z.infer<typeof WalletPropsSchema>

export class Wallet extends ValueObject<WalletProps> {
  /**
   * Gets the Accounts in the Wallet.
   * @returns The Accounts.
   */
  public getAccounts(): Account[] {
    return this.props.accounts
  }

  /**
   * Gets the Account with the given Address.
   * @returns The Account, or undefined if none.
   */
  private getAccountByAddress(address: Address): Account | undefined {
    return this.props.accounts.find(account =>
      account.getAddress().equals(address)
    )
  }

  /**
   * Removes the Account with the given Address.
   * @returns A new Wallet with the Account removed.
   */
  public removeAccountByAddress(
    address: Address
  ): ResultOrError<Wallet, AccountNotFoundError> {
    const account = this.props.accounts.find(account =>
      account.getAddress().equals(address)
    )
    if (!account) {
      return {
        success: false,
        error: new AccountNotFoundError(
          'Account not found while removing account'
        ),
      }
    }
    return { success: true, result: this.removeAccount(account) }
  }

  /**
   * Returns a new Wallet instance with the given account added.
   * @param account The account to add.
   * @returns A new Wallet instance with the given account added.
   * @throws If the account already exists in the wallet.
   */
  public addAccount(account: Account): Wallet {
    const accounts = [...this.props.accounts, account]
    return Wallet.create({ accounts })
  }

  /**
   * Returns a new Wallet instance with the given account removed.
   * @param account The account to remove.
   * @returns A new Wallet instance with the given account removed.
   */
  private removeAccount(account: Account): Wallet {
    const accounts = this.props.accounts.filter(a => a !== account)
    return Wallet.create({ accounts })
  }

  /**
   * Returns a new Wallet instance with the given accounts added.
   * @param accounts The accounts to add.
   * @returns A new Wallet instance with the given accounts added.
   */
  public restoreAccounts(
    accounts: Account[]
  ): ResultOrError<Wallet, KeyringSubdomainError> {
    // const newAccounts = accounts.filter(
    //   account => !this.getAccountByAddress(account.getAddress())
    // )
    // const wallet = this.addAccounts(newAccounts)
    // return { success: true, result: wallet }

    // TODO Implement this
    // TODO Add a `createdFrom` field for accounts with values 'backup' or 'locally'
    // TODO Add a way to skip accounts that already exist in the wallet. Consider returning a list of skipped accounts,
    //   if it's useful to the UI.
    console.log(accounts)
    return ResultOrError.success(this)
  }

  /**
   * Signs data with an intended validator address. See EIP-191 for more details.
   * @param dataToSign The data to sign.
   * @param signingAddress The address that signs the data.
   * @param validatorAddress The address that validates the data.
   * @returns An EIP-191 signature, or an error if the signing account was not found.
   */
  public signData(
    dataToSign: TransactionData,
    signingAddress: Address,
    validatorAddress: Address
  ): ResultOrError<EIP191Signature, AccountNotFoundError> {
    const signingAccount = this.getAccountByAddress(signingAddress)
    if (!signingAccount) {
      return {
        success: false,
        error: new AccountNotFoundError(
          'Signing Account not found while signing data'
        ),
      }
    }

    const privateKey = signingAccount.getPrivateKey()

    return {
      success: true,
      result: EIP191Signature.create({
        validatorAddress,
        dataToSign,
        privateKey,
      }),
    }
  }

  /**
   * Signs a transaction.
   * @param transactionRequest The transaction to sign.
   * @param signingAddress The address that signs the transaction.
   * @returns A signed transaction, or an error if the signing account was not found.
   */
  public signTransaction(
    transactionRequest: TransactionRequest,
    signingAddress: Address
  ): ResultOrError<SignedTransaction, AccountNotFoundError> {
    const signingAccount = this.getAccountByAddress(signingAddress)
    if (!signingAccount) {
      return {
        success: false,
        error: new AccountNotFoundError(
          'Signing Account not found while signing transaction'
        ),
      }
    }

    const privateKey = signingAccount.getPrivateKey()

    return {
      success: true,
      result: SignedTransaction.create({
        transactionRequest,
        privateKey,
      }),
    }
  }

  /**
   * Signs a message.
   * @param message The message to sign.
   * @param signingAddress The address that signs the message.
   * @returns A signed message, or an error if the signing account was not found.
   */
  public signMessage(
    message: MessageSigningRequest,
    signingAddress: Address
  ): ResultOrError<SignedMessage, AccountNotFoundError> {
    const signingAccount = this.getAccountByAddress(signingAddress)
    if (!signingAccount) {
      return {
        success: false,
        error: new AccountNotFoundError(
          'Signing Account not found while signing message'
        ),
      }
    }

    const privateKey = signingAccount.getPrivateKey()

    return {
      success: true,
      result: SignedMessage.create({ message, privateKey }),
    }
  }

  /**
   * Asserts that the given Accounts do not contain duplicates.
   */
  private static assertNoDuplicateAccounts(accounts: Account[]): void {
    const uniqueAccounts = accounts.filter(
      (account, index, self) => self.findIndex(a => a.equals(account)) === index
    )
    return assert(
      uniqueAccounts.length === accounts.length,
      'Wallet cannot contain duplicate accounts'
    )
  }

  /**
   * Creates a new Wallet instance. The wallet may not contain duplicate accounts.
   */
  public static create(props: WalletProps): Wallet {
    const validatedProps = WalletPropsSchema.parse(props)
    Wallet.assertNoDuplicateAccounts(props.accounts)
    return new Wallet(validatedProps)
  }

  public static createEmpty(): Wallet {
    return Wallet.create({ accounts: [] })
  }
}
